'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';

interface NavItem {
  label: string;
  href: string;
  isPage?: boolean;
  sectionId?: string;
}

const navItems: NavItem[] = [
  { label: 'Home', href: '#home', sectionId: 'home' },
  { label: 'Services', href: '#services', sectionId: 'services' },
  { label: 'Subscriptions', href: '#packages', sectionId: 'packages' },
  { label: 'About', href: '#reviews', sectionId: 'reviews' },
  { label: 'Contact', href: '#estimate', sectionId: 'estimate' },
  { label: 'Careers', href: '/careers', isPage: true },
];

const SMOOTH_SCROLL_DURATION = 500;

function smoothScrollToTop() {
  const start = window.scrollY;
  const startTime = performance.now();

  function step(currentTime: number) {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / SMOOTH_SCROLL_DURATION, 1);
    // Ease-in-out cubic
    const ease = progress < 0.5
      ? 4 * progress * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 3) / 2;
    window.scrollTo(0, start * (1 - ease));
    if (progress < 1) requestAnimationFrame(step);
  }

  requestAnimationFrame(step);
}

export default function RGPSHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const router = useRouter();
  const pathname = usePathname();

  const isHomePage = pathname === '/' || pathname === '/home';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Active section tracking via IntersectionObserver
  useEffect(() => {
    if (!isHomePage) return;

    const sectionIds = navItems
      .filter((item) => item.sectionId)
      .map((item) => item.sectionId as string);

    const observers: IntersectionObserver[] = [];

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(observerCallback, {
        rootMargin: '-40% 0px -55% 0px',
        threshold: 0,
      });
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, [isHomePage]);

  const closeMenuThen = useCallback((fn: () => void) => {
    setMenuOpen(false);
    // Small delay to let menu close animation start before scrolling
    setTimeout(fn, 150);
  }, []);

  const handleHomeClick = useCallback(() => {
    if (isHomePage) {
      closeMenuThen(() => smoothScrollToTop());
    } else {
      setMenuOpen(false);
      // Navigate to home page; scroll to top handled by page load
      router.push('/?scrollTop=1');
    }
  }, [isHomePage, closeMenuThen, router]);

  const scrollTo = useCallback((href: string) => {
    if (href === '#home') {
      handleHomeClick();
      return;
    }
    closeMenuThen(() => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    });
  }, [handleHomeClick, closeMenuThen]);

  // Handle ?scrollTop=1 query param — scroll to top after navigating home
  useEffect(() => {
    if (isHomePage && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('scrollTop') === '1') {
        // Remove the query param without reloading
        const url = window.location.pathname;
        window.history.replaceState({}, '', url);
        // Small delay to ensure page has rendered
        setTimeout(() => smoothScrollToTop(), 100);
      }
    }
  }, [isHomePage]);

  const isNavActive = (item: NavItem) => {
    if (item.isPage) return pathname === item.href;
    if (!isHomePage) return false;
    return activeSection === item.sectionId;
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white/97 backdrop-blur-md shadow-md py-3'
            : 'bg-transparent py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4">
          {/* Logo */}
          <button
            onClick={handleHomeClick}
            className="flex items-center gap-2.5 group flex-shrink-0"
            aria-label="Go to homepage"
          >
            <div className="w-10 h-10 rounded-xl bg-teal flex items-center justify-center shadow-teal">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="9 22 9 12 15 12 15 22" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="hidden sm:block">
              <div className={`font-bold text-sm leading-tight transition-colors ${scrolled ? 'text-dark-text' : 'text-white'}`}>
                Rio Grande
              </div>
              <div className={`text-xs font-medium leading-tight transition-colors ${scrolled ? 'text-teal' : 'text-teal-light/90'}`}>
                Property Solutions
              </div>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6">
            {navItems.map((item) => {
              const active = isNavActive(item);
              const baseClass = `text-sm font-semibold transition-colors hover:text-teal relative`;
              const colorClass = active
                ? 'text-teal'
                : scrolled
                ? 'text-gray-text' :'text-white/90';

              return item.isPage ? (
                <Link
                  key={item.label}
                  href={item.href}
                  className={`${baseClass} ${colorClass}`}
                >
                  {item.label}
                  {active && (
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-teal rounded-full" />
                  )}
                </Link>
              ) : (
                <button
                  key={item.label}
                  onClick={() => scrollTo(item.href)}
                  className={`${baseClass} ${colorClass}`}
                >
                  {item.label}
                  {active && (
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-teal rounded-full" />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Right CTAs */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Desktop Sign In / Sign Up buttons */}
            <Link
              href="/portal/login"
              className={`hidden lg:inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full border-2 border-[#0ABFBC] transition-all duration-200 hover:bg-[#0ABFBC]/10 hover:scale-105 ${
                scrolled ? 'bg-black text-white' : 'bg-black/70 text-white'
              }`}
            >
              Sign In
            </Link>
            <Link
              href="/portal/signup"
              className="hidden lg:inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full bg-[#0ABFBC] text-white shadow-lg shadow-[#0ABFBC]/30 transition-all duration-200 hover:bg-[#089A97] hover:scale-105 hover:shadow-[#0ABFBC]/50"
            >
              Sign Up
            </Link>

            {/* Phone - desktop */}
            <a
              href="tel:5059102846"
              className={`hidden md:flex items-center gap-1.5 text-sm font-semibold transition-colors hover:text-teal ${
                scrolled ? 'text-dark-text' : 'text-white'
              }`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
              </svg>
              (505) 910-2846
            </a>

            {/* Phone - mobile */}
            <a
              href="tel:5059102846"
              className={`flex md:hidden items-center justify-center w-9 h-9 rounded-full transition-all ${
                scrolled ? 'bg-teal text-white' : 'bg-white/20 text-white backdrop-blur-sm'
              }`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
              </svg>
            </a>

            {/* Get Estimate CTA */}
            <button
              onClick={() => scrollTo('#estimate')}
              className="hidden sm:flex items-center gap-1.5 bg-teal hover:bg-teal-dark text-white font-bold text-sm px-4 py-2.5 rounded-full transition-all shadow-teal hover:shadow-teal-lg animate-pulse-teal"
            >
              Get Free Estimate
            </button>

            {/* Hamburger */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`lg:hidden flex flex-col gap-1.5 p-2 rounded-lg transition-colors ${
                scrolled ? 'text-dark-text' : 'text-white'
              }`}
              aria-label="Toggle menu"
            >
              <span className={`block w-5 h-0.5 bg-current transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`block w-5 h-0.5 bg-current transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
              <span className={`block w-5 h-0.5 bg-current transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-300 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
        <div
          className={`absolute top-0 right-0 h-full w-72 bg-white shadow-2xl transition-transform duration-300 ${
            menuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="p-6 pt-20">
            {/* Mobile Sign In / Sign Up buttons — top of menu */}
            <div className="flex flex-col gap-3 mb-5">
              <Link
                href="/portal/login"
                onClick={() => setMenuOpen(false)}
                className="w-full flex items-center justify-center gap-2 bg-black text-white font-bold py-3.5 rounded-full border-2 border-[#0ABFBC] text-base transition-all duration-200 hover:bg-[#0ABFBC]/10"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/>
                </svg>
                Sign In
              </Link>
              <Link
                href="/portal/signup"
                onClick={() => setMenuOpen(false)}
                className="w-full flex items-center justify-center gap-2 bg-[#0ABFBC] text-white font-bold py-3.5 rounded-full text-base shadow-lg shadow-[#0ABFBC]/30 transition-all duration-200 hover:bg-[#089A97]"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
                </svg>
                Sign Up
              </Link>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 mb-4" />

            <nav className="flex flex-col gap-1">
              {navItems.map((item) => {
                const active = isNavActive(item);
                const itemClass = `text-left px-4 py-3 font-semibold text-base rounded-xl transition-all ${
                  active
                    ? 'bg-teal text-white' :'text-dark-text hover:bg-teal-light hover:text-teal'
                }`;

                return item.isPage ? (
                  <Link
                    key={item.label}
                    href={item.href}
                    onClick={() => setMenuOpen(false)}
                    className={itemClass}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.label}
                    onClick={() => scrollTo(item.href)}
                    className={itemClass}
                  >
                    {item.label}
                  </button>
                );
              })}
            </nav>
            <div className="mt-6 flex flex-col gap-3">
              <button
                onClick={() => scrollTo('#estimate')}
                className="w-full bg-teal text-white font-bold py-3.5 rounded-full text-center shadow-teal"
              >
                Get Free Estimate
              </button>
              <a
                href="tel:5059102846"
                className="w-full border-2 border-teal text-teal font-bold py-3.5 rounded-full text-center"
              >
                📞 (505) 910-2846
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
